import React, { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import CommentsTable from './components/CommentsTable';
import CommentForm from './components/CommentForm';
import { commentService } from './services/commentService';
import { parseUrlParams, validateParams } from './utils/urlUtils';
import './styles/App.css';

const App = () => {
  const [params, setParams] = useState({
    retailAssoc: '',
    coa: '',
    period: '',
    username: '',
  });
  
  const [form, setForm] = useState({
    username: '',
    lineItem: '',
    comment: '',
  });
  
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [paramsError, setParamsError] = useState(false);

  // Use useCallback to fix the React Hook warning
  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await commentService.getComments(params);
      const sorted = [...data].sort((a, b) => 
        new Date(b.created) - new Date(a.created)
      );
      setComments(sorted);
      setSubmitError('');
    } catch (error) {
      console.error('Error fetching comments:', error);
      setSubmitError('Failed to load comments. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    const urlParams = parseUrlParams();
    
    // DEBUG: Log parsed URL parameters
    console.log('Parsed URL Parameters:', urlParams);
    console.log('Current URL:', window.location.href);
    
    setParams({
      retailAssoc: urlParams.retailAssoc,
      coa: urlParams.coa,
      period: urlParams.period,
      username: urlParams.username,
    });
    setParamsError(!validateParams(urlParams));
    
    // Pre-populate form with URL parameters
    setForm({
      username: urlParams.username,
      lineItem: urlParams.lineitem,
      comment: urlParams.comment,
    });
  }, []);

  useEffect(() => {
    if (validateParams(params)) {
      fetchComments();
    }
  }, [params, fetchComments]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!form.username.trim()) {
      newErrors.username = 'User Name is mandatory';
    }
    if (!form.lineItem.trim()) {
      newErrors.lineItem = 'Line Item is mandatory';
    }
    if (!form.comment.trim()) {
      newErrors.comment = 'Comment is mandatory';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async () => {
    setSubmitError('');
    
    if (!validateForm()) {
      setSubmitError('Please enter information in all fields before saving the comment.');
      return;
    }
    
    setLoading(true);
    try {
      await commentService.createComment({
        userName: form.username,
        comment: form.comment,
        retailAssoc: params.retailAssoc,
        coa: params.coa,
        period: params.period,
        lineItem: form.lineItem,
      });
      
      setForm({
        username: form.username,
        lineItem: '',
        comment: '',
      });
      setErrors({});
      await fetchComments();
    } catch (error) {
      console.error('Error saving comment:', error);
      setSubmitError('Error occurred on persisting the new comment. Please try again and if this problem persists contact your system administrator.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app">
      <div className="app-container">
        <Header params={params} paramsError={paramsError} />
        
        <CommentsTable 
          comments={comments}
          loading={loading}
          onRefresh={fetchComments}
          params={params}
        />
        
        <CommentForm
          form={form}
          errors={errors}
          submitError={submitError}
          loading={loading}
          paramsError={paramsError}
          onChange={handleFormChange}
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  );
};

export default App;
